import { useEffect, useState } from 'react';
import { X, Users, Send, CalendarDays } from 'lucide-react';
import StaffPicker from './StaffPicker';
import { useFormGuard } from '../hooks/useFormGuard';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

export default function DelegateModal({ item, onClose, onDelegate }) {
  useBodyScrollLock();
  const [staffId, setStaffId] = useState(null);
  const [delegatedTo, setDelegatedTo] = useState('');
  const [notes, setNotes] = useState('');
  const [expectedReturn, setExpectedReturn] = useState('');
  const [isAdHoc, setIsAdHoc] = useState(false);
  const [adHocName, setAdHocName] = useState('');

  const isDirty = delegatedTo !== '' || adHocName !== '' || notes !== '';
  const { guard, showDialog, discard, cancel } = useFormGuard(isDirty);
  const guardedClose = () => guard(onClose);

  // ESC closes the modal through the same unsaved-changes guard as the X/Cancel
  // buttons. Gated on !showDialog so ESC doesn't re-trigger while the
  // unsaved-changes confirm dialog is already up (it owns its own keys).
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !showDialog) guardedClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  const handleSubmit = () => {
    const name = isAdHoc ? adHocName : delegatedTo;
    if (!name) return;
    onDelegate(item, name, notes, isAdHoc ? null : staffId, expectedReturn, isAdHoc);
  };

  const canSubmit = isAdHoc ? adHocName.trim().length > 0 : !!delegatedTo;

  return (
    <div data-testid="delegate-modal-overlay" className="fixed inset-0 z-50 overflow-y-auto command-backdrop">
      <div className="flex min-h-full items-center justify-center px-4 py-8" onClick={(e) => { if (e.target === e.currentTarget) guardedClose(); }}>
      <div className="w-[min(92vw,520px)] rounded-[24px] overflow-hidden animate-fade-in pd-glass" style={{ boxShadow: 'var(--pd-shadow-3)' }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--pd-border)' }}>
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" style={{ color: 'var(--pd-warning)' }} />
            <span className="text-sm font-semibold" style={{ color: 'var(--pd-text)' }}>Delegate task</span>
          </div>
          <button data-testid="delegate-modal-close" onClick={guardedClose} className="pd-icon-button" style={{ width: 32, height: 32 }}><X className="w-4 h-4" /></button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-xl p-3" style={{ background: 'var(--pd-panel-2)' }}>
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>Task</div>
            <div className="text-sm font-medium mt-1" style={{ color: 'var(--pd-text)' }}>{item?.title}</div>
            {item?.priority && <span className="text-[9px] font-semibold" style={{ color: 'var(--pd-muted)' }}>{item.priority} · {item.estimatedMinutes || 30}m</span>}
          </div>

          {/* Toggle: Staff vs Ad-hoc */}
          <div className="flex gap-2">
            <button type="button" data-testid="delegate-mode-staff" onClick={() => setIsAdHoc(false)}
              className="flex-1 px-3 py-2 rounded-xl text-[11px] font-semibold text-center"
              style={{ background: !isAdHoc ? 'var(--pd-text)' : 'var(--pd-panel-2)', color: !isAdHoc ? 'white' : 'var(--pd-muted)' }}>
              Your staff
            </button>
            <button type="button" data-testid="delegate-mode-adhoc" onClick={() => setIsAdHoc(true)}
              className="flex-1 px-3 py-2 rounded-xl text-[11px] font-semibold text-center"
              style={{ background: isAdHoc ? 'var(--pd-text)' : 'var(--pd-panel-2)', color: isAdHoc ? 'white' : 'var(--pd-muted)' }}>
              Misc / Ad-hoc person
            </button>
          </div>

          {isAdHoc ? (
            <div>
              <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}>Person name</label>
              <input data-testid="delegate-adhoc-name" value={adHocName} onChange={(e) => setAdHocName(e.target.value)}
                placeholder="e.g. Courthouse clerk, CA Gupta, courier..."
                className="w-full px-3 py-2.5 rounded-[16px] text-sm outline-none"
                style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)', color: 'var(--pd-text)' }}
                autoFocus />
            </div>
          ) : (
            <StaffPicker
              value={delegatedTo}
              staffId={staffId}
              businessId={item?.businessId}
              onChange={({ staffId: sid, delegatedTo: name }) => { setStaffId(sid); setDelegatedTo(name); }}
            />
          )}

          {/* Expected return date */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.08em] font-semibold flex items-center gap-1 mb-1.5" style={{ color: 'var(--pd-subtle)' }}>
              <CalendarDays className="w-3 h-3" />Expected return date
            </label>
            <input data-testid="delegate-return-date" type="date" value={expectedReturn} onChange={(e) => setExpectedReturn(e.target.value)}
              className="w-full px-3 py-2.5 rounded-[16px] text-sm outline-none"
              style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)', color: 'var(--pd-text)' }} />
          </div>

          {/* Notes */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.08em] font-semibold block mb-1.5" style={{ color: 'var(--pd-subtle)' }}>Notes (optional)</label>
            <textarea data-testid="delegate-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Instructions or context..." rows={2}
              className="w-full px-3 py-2.5 rounded-[16px] text-sm outline-none resize-none"
              style={{ border: '1px solid var(--pd-border)', background: 'var(--pd-panel-solid)', color: 'var(--pd-text)' }} />
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={guardedClose} className="px-4 py-2 rounded-xl text-[12px] font-medium" style={{ color: 'var(--pd-muted)' }}>Cancel</button>
            <button data-testid="delegate-confirm-button" type="button" onClick={handleSubmit} disabled={!canSubmit}
              className="flex items-center gap-1.5 px-5 py-2 rounded-xl text-[12px] font-semibold text-white btn-primary disabled:opacity-40">
              <Send className="w-3 h-3" />Delegate
            </button>
          </div>
        </div>
      </div>
      </div>
      <UnsavedChangesDialog open={showDialog} onDiscard={discard} onCancel={cancel} />
    </div>
  );
}
